import { DialogComponent } from '@theme/dialog';

const HASH = '#be-map-overlay';
const TRIGGER_SELECTOR = `a[href$="${HASH}"]`;

/**
 * The map overlay: a hidden, pre-rendered white dialog
 * (sections/map-overlay.liquid) holding a single merchant-managed Image
 * block, rendered full width inside the panel.
 *
 * Opens from two kinds of trigger (pattern shared with the booking overlay):
 * - any anchor whose href ends in `#be-map-overlay`, followed in place of
 *   navigating — so a link added anywhere on the page works (button blocks,
 *   rich-text links, menu links), with no bespoke markup needed;
 * - a `#be-map-overlay` URL hash on load, making e.g.
 *   /pages/land#be-map-overlay a deep link that works from other pages.
 *   The hash is stripped once consumed so a refresh after closing doesn't
 *   reopen it.
 *
 * An on-page trigger link is a visible element, so the native <dialog> focus
 * restore returns focus to it on close; no custom focus handling here.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends DialogComponent
 */
class MapOverlayComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#handleTriggerClick);
    this.#openFromHash();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleTriggerClick);
  }

  #openFromHash() {
    if (window.location.hash !== HASH) return;

    history.replaceState(null, '', window.location.pathname + window.location.search);
    this.showDialog();
  }

  /**
   * Opens the overlay in place of following a trigger link (the link's
   * #be-map-overlay href is an inert no-JS fallback).
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleTriggerClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest(TRIGGER_SELECTOR);

    if (!link || this.contains(link)) return;

    event.preventDefault();
    this.showDialog();
  };
}

if (!customElements.get('be-map-overlay-component')) {
  customElements.define('be-map-overlay-component', MapOverlayComponent);
}
